import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import DashboardWrapper from '../../../Shared/DashboardWrapper';
import { SkeletonText } from '../../../Shared/Skeletons';
import BackLink from '../../MainDashboard/Common/BackLink';
import { getExternalLink } from '../../../../lib/api/external-link';
import { getEnquiryFunctionalityFeatureFlag } from '../../../../lib/api/enquiry-functionality';
import { getPortalDocumentUploadFeatureFlag } from '../../../../lib/api/document-upload';
import { fetchEnquiryFormStructure, recordSelfServeDeflection, submitEnquiry } from '../../../../lib/api/enquiry-form';
import { fetchCustomerSites } from '../../../../lib/api/customer-sites';
import { getCustomerBusinessName } from '../../../../lib/api/localstorage';
import { useDocumentUploads } from '../../../../lib/hooks/useDocumentUploads';
import DocumentUploadArea from './DocumentUploadArea';
import { documentsForSubmit, submitBlockedMessage, submitBlockers } from './documentUpload';
import {
    STEPS,
    MIN_DESCRIPTION_LENGTH,
    ACCOUNT_QUERY_CHOICE,
    ACCOUNT_QUERY_LABEL,
    applyOptionChoice,
    applyQueryTypeChoice,
    applySiteChoice,
    applyUtilityChoice,
    autoSiteName,
    clearDraft,
    createDraft,
    findSelection,
    getOptions,
    getQueryTypes,
    getSites,
    getUtilities,
    initialStepFor,
    isDescriptionValid,
    isRaiseEnquiryOption,
    loadDraft,
    reconcileDraftSite,
    resolveSelfServeDestination,
    resolveSiteChoice,
    saveDraft,
    siteChoiceRequired,
} from './raiseEnquiryForm';

export default function RaiseEnquiry() {
    const [draft, setDraft] = useState(() => loadDraft(window.sessionStorage) || createDraft());
    const [step, setStep] = useState(1);
    const [structure, setStructure] = useState(null);
    const [structureLoading, setStructureLoading] = useState(true);
    const [structureError, setStructureError] = useState(false);
    const [sites, setSites] = useState(null);
    const [sitesLoading, setSitesLoading] = useState(true);
    const [sitesError, setSitesError] = useState(false);
    const [siteError, setSiteError] = useState('');
    const [descriptionError, setDescriptionError] = useState('');
    const [submitError, setSubmitError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [helpLink, setHelpLink] = useState('');
    const [deflectionRecorded, setDeflectionRecorded] = useState(false);
    const [documentUploadEnabled, setDocumentUploadEnabled] = useState(false);
    const history = useHistory();
    const uploads = useDocumentUploads({
        enabled: documentUploadEnabled,
        draft,
        setDraft,
        submissionReference: draft.submissionReference,
    });

    useEffect(() => {
        getEnquiryFunctionalityFeatureFlag()
            .then((resp) => {
                if (resp?.data?.data !== true) history.push('/dashboard/main');
            })
            .catch(() => {
                history.push('/dashboard/main');
            });
    }, [history]);

    useEffect(() => {
        getPortalDocumentUploadFeatureFlag()
            .then((resp) => setDocumentUploadEnabled(resp?.data?.data === true))
            .catch(() => setDocumentUploadEnabled(false));
    }, []);

    useEffect(() => {
        getExternalLink()
            .then((resp) => setHelpLink(resp.data.faqLink))
            .catch(() => setHelpLink(''));
    }, []);

    useEffect(() => {
        let cancelled = false;
        setStructureLoading(true);
        setStructureError(false);
        fetchEnquiryFormStructure()
            .then((data) => {
                if (cancelled) return;
                setStructure(data?.data || null);
                setStructureLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                if (err?.response?.status === 404) {
                    history.push('/dashboard/main');
                    return;
                }
                setStructureError(true);
                setStructureLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [history]);

    useEffect(() => {
        let cancelled = false;
        setSitesLoading(true);
        setSitesError(false);
        fetchCustomerSites()
            .then((data) => {
                if (cancelled) return;
                setSites(getSites(data?.data));
                setSitesLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                if (err?.response?.status === 404) {
                    history.push('/dashboard/main');
                    return;
                }
                setSitesError(true);
                setSitesLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [history]);

    useEffect(() => {
        if (Array.isArray(sites)) setDraft((current) => reconcileDraftSite(current, sites));
    }, [sites]);

    useEffect(() => {
        saveDraft(window.sessionStorage, draft);
    }, [draft]);

    useEffect(() => {
        if (structure) setStep(initialStepFor(draft, structure));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [structure]);

    useEffect(() => {
        setDeflectionRecorded(false);
    }, [draft.optionDomainId, step]);

    const utilities = useMemo(() => getUtilities(structure), [structure]);
    const queryTypes = useMemo(
        () => getQueryTypes(structure, draft.utilityDomainId),
        [structure, draft.utilityDomainId]
    );
    const options = useMemo(
        () => getOptions(structure, draft.utilityDomainId, draft.queryTypeDomainId),
        [structure, draft.utilityDomainId, draft.queryTypeDomainId]
    );
    const selection = useMemo(() => findSelection(structure, draft), [structure, draft]);

    const businessName = getCustomerBusinessName();

    const chooseUtility = (utilityDomainId) => setDraft((current) => applyUtilityChoice(current, utilityDomainId));
    const chooseQueryType = (queryTypeDomainId) => setDraft((current) => applyQueryTypeChoice(current, queryTypeDomainId));
    const chooseOption = (optionDomainId) => setDraft((current) => applyOptionChoice(current, optionDomainId));
    const chooseSite = (value) => {
        setSiteError('');
        setDraft((current) => applySiteChoice(current, value));
    };
    const changeDescription = (value) => {
        setDescriptionError('');
        setDraft((current) => ({ ...current, description: value }));
    };

    const continueFromUtility = () => {
        if (selection.utility) setStep(2);
    };

    const continueFromOption = () => {
        if (selection.option) setStep(3);
    };

    const stepBack = () => {
        setSubmitError('');
        setDescriptionError('');
        setSiteError('');
        setStep((current) => Math.max(1, current - 1));
    };

    const send = () => {
        if (!isDescriptionValid(draft.description)) {
            setDescriptionError(`Please tell us about your enquiry in at least ${MIN_DESCRIPTION_LENGTH} characters`);
            return;
        }
        const siteChoice = resolveSiteChoice(sites, draft);
        if (!siteChoice) {
            setSiteError('Please choose which site this enquiry concerns.');
            return;
        }
        const documentRows = documentUploadEnabled ? uploads.rows : [];
        const blockers = submitBlockers(documentRows);
        if (blockers.length > 0) {
            setSubmitError(submitBlockedMessage(blockers));
            return;
        }
        const documents = documentsForSubmit(documentRows);
        setSubmitError('');
        setSubmitting(true);
        submitEnquiry({
            enquiryOptionDomainId: draft.optionDomainId,
            description: draft.description.trim(),
            submissionReference: draft.submissionReference,
            siteDomainId: siteChoice.siteDomainId,
            accountQuery: siteChoice.accountQuery,
            documents,
        })
            .then((resp) => {
                const reference = resp?.data?.data?.enquiryReference;
                history.replace(`/enquiries/${reference || 'unknown'}/confirmation`);
            })
            .catch((err) => {
                setSubmitting(false);
                if (err?.response?.status === 404) {
                    history.push('/dashboard/main');
                    return;
                }
                const refusedFiles = err?.response?.data?.errors;
                if (documents.length > 0 && err?.response?.data?.statusCode === 409 && Array.isArray(refusedFiles) && refusedFiles.length > 0) {
                    uploads.reloadStatuses();
                    setSubmitError(`Some files can't be sent: ${refusedFiles.join('; ')}. Please remove or replace them and try again.`);
                    return;
                }
                setSubmitError('We could not send your enquiry. Your answers have been kept - please try again.');
            });
    };

    const trailChips = [];
    if (selection.utility) trailChips.push({ id: 'raise-enquiry-trail-utility', label: selection.utility.name, step: 1 });
    if (selection.queryType) trailChips.push({ id: 'raise-enquiry-trail-query-type', label: selection.queryType.name, step: 2 });
    if (selection.option) trailChips.push({ id: 'raise-enquiry-trail-option', label: selection.option.name, step: 2 });

    const detailsStepIsSelfServe = step === 3 && selection.option && !isRaiseEnquiryOption(selection.option);
    const selfServeDestination = detailsStepIsSelfServe ? resolveSelfServeDestination(selection.option, helpLink) : null;

    const recordDeflection = () => {
        if (deflectionRecorded) return;
        setDeflectionRecorded(true);
        recordSelfServeDeflection(draft.optionDomainId).catch(() => {});
    };
    const stepperStep = step === 1 ? 1 : step === 2 ? (selection.option ? 3 : 2) : 4;
    const formLoading = structureLoading || sitesLoading;
    const formError = structureError || sitesError;

    return (
        <DashboardWrapper helpLink={helpLink}>
            <div className="main-dashboard" id="raise-enquiry-page">
                <BackLink />

                <section className="enquiries__heading" id="raise-enquiry-heading">
                    <h1>Raise an enquiry</h1>
                </section>

                <div className="raise-enquiry__stepper" id="raise-enquiry-stepper">
                    {STEPS.map((stepInfo) => (
                        <div
                            key={stepInfo.number}
                            id={`raise-enquiry-step-${stepInfo.number}`}
                            className={`raise-enquiry__step${stepperStep === stepInfo.number ? ' raise-enquiry__step--active' : ''}${stepperStep > stepInfo.number ? ' raise-enquiry__step--done' : ''}`}
                        >
                            <span className="raise-enquiry__step-number">
                                {stepperStep > stepInfo.number ? '✓' : stepInfo.number}
                            </span>
                            <span className="raise-enquiry__step-label">{stepInfo.label}</span>
                        </div>
                    ))}
                </div>

                {businessName && (
                    <div className="raise-enquiry__note" id="raise-enquiry-signed-in-note">
                        Signed in as <strong>{businessName}</strong> — this enquiry will be linked to your account
                        automatically. We just need to know what it's about.
                    </div>
                )}

                {trailChips.length > 0 && step > 1 && (
                    <div className="raise-enquiry__trail" id="raise-enquiry-trail">
                        {trailChips.map((chip) => (
                            <button
                                key={chip.id}
                                type="button"
                                id={chip.id}
                                className="raise-enquiry__chip"
                                onClick={() => setStep(chip.step)}
                            >
                                {chip.label}
                            </button>
                        ))}
                    </div>
                )}

                {formLoading && (
                    <div id="raise-enquiry-loading">
                        <SkeletonText className="w-2/3" />
                        <SkeletonText className="w-1/2" />
                        <SkeletonText className="w-2/3" />
                    </div>
                )}

                {!formLoading && formError && (
                    <div className="enquiries__empty" id="raise-enquiry-error">
                        There was an error loading the enquiry form, please refresh the page to try again.
                    </div>
                )}

                {!formLoading && !formError && step === 1 && (
                    <section id="raise-enquiry-utility-step">
                        <h2 className="raise-enquiry__question">Which service is this about?</h2>
                        <div className="raise-enquiry__tiles" id="raise-enquiry-utility-tiles">
                            {utilities.map((utility) => (
                                <button
                                    key={utility.domainId}
                                    type="button"
                                    id={`raise-enquiry-utility-${utility.name.toLowerCase()}`}
                                    className={`raise-enquiry__tile${draft.utilityDomainId === utility.domainId ? ' raise-enquiry__tile--selected' : ''}`}
                                    onClick={() => chooseUtility(utility.domainId)}
                                >
                                    {utility.name}
                                </button>
                            ))}
                        </div>
                        <div className="raise-enquiry__footer">
                            <button
                                type="button"
                                className="raise-enquiry__cancel"
                                id="raise-enquiry-cancel"
                                onClick={() => history.push('/enquiries')}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="enquiries__raise-btn"
                                id="raise-enquiry-continue-utility"
                                disabled={!selection.utility}
                                onClick={continueFromUtility}
                            >
                                Continue
                            </button>
                        </div>
                    </section>
                )}

                {!formLoading && !formError && step === 2 && (
                    <section id="raise-enquiry-option-step">
                        <div className="raise-enquiry__columns">
                            <div className="raise-enquiry__column">
                                <h2 className="raise-enquiry__question">What type of query?</h2>
                                <select
                                    id="raise-enquiry-query-type"
                                    className="enquiries__select"
                                    value={draft.queryTypeDomainId || ''}
                                    onChange={(ev) => chooseQueryType(ev.target.value || null)}
                                >
                                    <option value="">Choose a query type…</option>
                                    {queryTypes.map((queryType) => (
                                        <option key={queryType.domainId} value={queryType.domainId}>
                                            {queryType.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="raise-enquiry__column">
                                <h2 className="raise-enquiry__question">Choose the closest option</h2>
                                {!draft.queryTypeDomainId && (
                                    <p className="raise-enquiry__hint" id="raise-enquiry-options-hint">
                                        Choose a query type to see the options.
                                    </p>
                                )}
                                <div className="raise-enquiry__options" id="raise-enquiry-options">
                                    {options.map((option, index) => (
                                        <label
                                            key={option.domainId}
                                            className={`raise-enquiry__option${draft.optionDomainId === option.domainId ? ' raise-enquiry__option--selected' : ''}`}
                                            htmlFor={`raise-enquiry-option-${index}`}
                                        >
                                            <input
                                                type="radio"
                                                id={`raise-enquiry-option-${index}`}
                                                name="raise-enquiry-option"
                                                checked={draft.optionDomainId === option.domainId}
                                                onChange={() => chooseOption(option.domainId)}
                                            />
                                            <span>{option.name}</span>
                                            {!isRaiseEnquiryOption(option) && (
                                                <span className="raise-enquiry__self-serve-chip">self-serve ↗</span>
                                            )}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="raise-enquiry__footer">
                            <button type="button" className="raise-enquiry__cancel" id="raise-enquiry-back-to-utility" onClick={stepBack}>
                                Back
                            </button>
                            <button
                                type="button"
                                className="enquiries__raise-btn"
                                id="raise-enquiry-continue-option"
                                disabled={!selection.option}
                                onClick={continueFromOption}
                            >
                                Continue
                            </button>
                        </div>
                    </section>
                )}

                {!formLoading && !formError && step === 3 && detailsStepIsSelfServe && selfServeDestination && (
                    <section className="raise-enquiry__self-serve" id="raise-enquiry-self-serve">
                        <h2 className="raise-enquiry__question">You can do this right now</h2>
                        <p id="raise-enquiry-self-serve-text">
                            "{selection.option.name}" is something you can do yourself in the portal, so you don't need
                            to raise an enquiry. {selfServeDestination.guidance}
                        </p>
                        <p className="raise-enquiry__hint" id="raise-enquiry-self-serve-hint">
                            The page opens in a new tab, so this form stays open if you need anything else.
                        </p>
                        <div className="raise-enquiry__footer">
                            <button type="button" className="raise-enquiry__cancel" id="raise-enquiry-self-serve-back" onClick={stepBack}>
                                My query is about something else
                            </button>
                            <a
                                href={selfServeDestination.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="enquiries-tile__btn"
                                id="raise-enquiry-self-serve-go"
                                onClick={recordDeflection}
                            >
                                {selfServeDestination.ctaLabel} ↗
                            </a>
                        </div>
                    </section>
                )}

                {!formLoading && !formError && step === 3 && !detailsStepIsSelfServe && (
                    <section id="raise-enquiry-details-step">
                        {siteChoiceRequired(sites) && (
                            <div className="raise-enquiry__site" id="raise-enquiry-site-block">
                                <h2 className="raise-enquiry__question">Which site does this concern?</h2>
                                <select
                                    id="raise-enquiry-site"
                                    className="enquiries__select"
                                    value={draft.accountQuery ? ACCOUNT_QUERY_CHOICE : draft.siteDomainId || ''}
                                    onChange={(ev) => chooseSite(ev.target.value || null)}
                                >
                                    <option value="">Choose a site…</option>
                                    {sites.map((site) => (
                                        <option key={site.siteDomainId} value={site.siteDomainId}>
                                            {site.siteName}
                                        </option>
                                    ))}
                                    <option value={ACCOUNT_QUERY_CHOICE}>{ACCOUNT_QUERY_LABEL}</option>
                                </select>
                                <p className="raise-enquiry__hint" id="raise-enquiry-site-hint">
                                    {`Covers every site linked to your account — all utilities — plus "${ACCOUNT_QUERY_LABEL}" if it isn't about one site.`}
                                </p>
                                {siteError && (
                                    <div className="px-28px py-10px text-everflow-magenta text-13px" id="raise-enquiry-site-error">
                                        {siteError}
                                    </div>
                                )}
                            </div>
                        )}
                        {!siteChoiceRequired(sites) && autoSiteName(sites) && (
                            <div className="raise-enquiry__note" id="raise-enquiry-site-auto-note">
                                Site <strong>{autoSiteName(sites)}</strong> will be recorded automatically.
                            </div>
                        )}
                        <h2 className="raise-enquiry__question">Tell us about your enquiry</h2>
                        <textarea
                            id="raise-enquiry-description"
                            className={`raise-enquiry__textarea${descriptionError ? ' raise-enquiry__textarea--error' : ''}`}
                            rows={6}
                            value={draft.description}
                            onChange={(ev) => changeDescription(ev.target.value)}
                        />
                        <p className="raise-enquiry__hint" id="raise-enquiry-description-hint">
                            Include dates, invoice numbers and amounts so we can answer first time.
                        </p>
                        {descriptionError && (
                            <div className="px-28px py-10px text-everflow-magenta text-13px" id="raise-enquiry-description-error">
                                {descriptionError}
                            </div>
                        )}
                        {documentUploadEnabled && (
                            <DocumentUploadArea
                                rows={uploads.rows}
                                refused={uploads.refused}
                                onAddFiles={uploads.addFiles}
                                onRemove={uploads.remove}
                                onReplace={uploads.replace}
                                disabled={submitting}
                                connectionState={uploads.connectionState}
                            />
                        )}
                        <div className="raise-enquiry__note" id="raise-enquiry-immutable-note">
                            Check everything is right — you won't be able to edit this enquiry after you send it.
                        </div>
                        {submitError && (
                            <div className="px-28px py-10px text-everflow-magenta text-13px" id="raise-enquiry-submit-error">
                                {submitError}
                            </div>
                        )}
                        <div className="raise-enquiry__footer">
                            <button
                                type="button"
                                className="raise-enquiry__cancel"
                                id="raise-enquiry-back-to-options"
                                onClick={stepBack}
                                disabled={submitting}
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                className="enquiries__raise-btn"
                                id="raise-enquiry-send"
                                disabled={submitting}
                                onClick={send}
                            >
                                {submitting ? 'Sending…' : 'Send enquiry'}
                            </button>
                        </div>
                    </section>
                )}
            </div>
        </DashboardWrapper>
    );
}
